import React, { FC, useState, useRef, useEffect, useCallback, useContext } from 'react';
import { getImageNameWithoutExtensions } from '../../../shared/Helpers';
import { fabric } from 'fabric';
import { useCanvasSizeChange } from '../../StitchingTool/Canvas/Hooks';
import { useCanvasEvents } from './Hooks/UseCanvasEvents';
import { addToHistory, updated, sizeChanged } from './stitchedView.slice';
import { AppDispatch } from '../../../store';
import { RootState } from '../../../rootReducer';
import { connect } from 'react-redux';
import useSetImages from './Hooks/UseSetImages';
import { setImagesLoaded, setImagesLoading } from '../Annotation/annotation.slice';
import { setUnselectedImageProperties, setSelectedImageProperties, setSelectedFindingProperties } from '../Canvas/Helpers';
import { SequenceImage } from '../../StitchingTool/Stitching/Stitching.reducer';
import { RouteComponentProps, withRouter } from 'react-router';
import { ParserContext } from '../contexts/ParserContext';

type StitchedViewProps = {
  id: string;
  rawImages: SequenceImage[] | null;
  isStitched: boolean;
  resetCanvas: boolean;
  onImageObjectSelected: (obj?: fabric.Object) => void;
  setStitchingCanvas?: (canvas: fabric.Canvas) => void;
};

type StitchedViewStoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const StitchedView: FC<StitchedViewStoreProps & StitchedViewProps & RouteComponentProps> = ({
  history,
  id,
  selectedName,
  rawImages,
  isStitched,
  resetCanvas,
  fabricImages,
  zoom,
  sequenceInfo,
  subsampleFactor,
  canvasHeight,
  selectedAnnotationId,
  toggleAllUserFindingsVisibility,
  toggleMLFindingsVisibility,
  onImagesLoaded,
  onImageObjectSelected,
  setStitchingCanvas,
  onAddToHistory,
  onUpdate,
  onSizeChanged,
  startImagesLoading
}) => {
  const [canvasLoaded, setCanvasLoaded] = useState<boolean>(false);
  const [canvasEl, setCanvasEl] = useState<Nullable<fabric.Canvas>>(null);

  const canvasElRef = useRef<HTMLCanvasElement>(null);

  const { parser } = useContext(ParserContext);

  const mapActiveObjectsAndAddToHistory = useCallback(
    (activeObjects: fabric.Object[], skipSmallMovements: boolean) => {
      onAddToHistory(activeObjects, skipSmallMovements);
    },
    [onAddToHistory]
  );

  const sizeChangedHandler = useCallback(
    (canvasWidth, canvasHeight) => {
      onSizeChanged(canvasWidth, canvasHeight);
    },
    [onSizeChanged]
  );

  const imagesLoaded = useCallback(
    (loaded: boolean) => {
      setCanvasLoaded(loaded);

      if (loaded) {
        onImagesLoaded();
      }
    },
    [onImagesLoaded]
  );

  const navigateToFinding = useCallback(
    (newSequencePath: string, imageName: string, id: string) => {
      history.push(`/annotation-manager/${parser.name}/${newSequencePath}/${imageName}/${id}`);
    },
    [history, parser.name]
  );

  useEffect(() => {
    const newCanvas = new fabric.Canvas(canvasElRef.current!);
    setCanvasEl(newCanvas);
  }, [setStitchingCanvas]);

  useEffect(() => {
    if (!canvasLoaded && canvasHeight) {
      onUpdate(rawImages, isStitched, subsampleFactor);
    }
  }, [isStitched, rawImages, resetCanvas, onUpdate, canvasLoaded, canvasHeight]);

  useEffect(() => {
    setCanvasLoaded(false);
  }, [rawImages]);

  useEffect(() => {
    if (canvasEl) {
      canvasEl.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvasEl.setZoom(1 / zoom);
    }
  }, [canvasEl, zoom]);

  useEffect(() => {
    if (canvasEl && selectedName) {
      const all = canvasEl.getObjects();
      const selected = all.find(x => getImageNameWithoutExtensions(x.name!) === getImageNameWithoutExtensions(selectedName));
      if (selected) {
        all.forEach(element => {
          setUnselectedImageProperties(element, canvasEl);
        });

        setSelectedImageProperties(selected, canvasEl);

        canvasEl.renderAll();
      }
    }
  }, [selectedName, canvasEl, canvasLoaded]);

  useCanvasEvents(canvasEl, sequenceInfo, mapActiveObjectsAndAddToHistory, navigateToFinding, onImageObjectSelected);
  useSetImages(
    canvasEl,
    fabricImages,
    imagesLoaded,
    sequenceInfo,
    subsampleFactor,
    canvasLoaded,
    startImagesLoading,
    toggleAllUserFindingsVisibility,
    toggleMLFindingsVisibility
  );
  useCanvasSizeChange(canvasEl, sizeChangedHandler, 'canvas-container');

  useEffect(() => {
    if (canvasEl) {
      const allObjects = canvasEl.getObjects();
      const selectedObject = allObjects.find(x => x.name === selectedAnnotationId);
      if (selectedObject) {
        canvasEl.setActiveObject(selectedObject);
        setSelectedFindingProperties(selectedObject);
        canvasEl.renderAll();

        if (sequenceInfo) {
          const image = sequenceInfo.images.find(x => !!(x.annotations && x.annotations.find(y => y.id === selectedAnnotationId)));
          if (image) {
            const object = canvasEl.getObjects().find(x => getImageNameWithoutExtensions(x.name!) === getImageNameWithoutExtensions(image.filename));
            if (object) {
              onImageObjectSelected(object);
            }
          }
        }
      } else {
        canvasEl.discardActiveObject();
        canvasEl.renderAll();
      }
    }
  }, [canvasEl, onImageObjectSelected, selectedAnnotationId, sequenceInfo]);

  return (
    <div id='canvas-container' style={{ height: '84vh', overflow: 'hidden' }}>
      <canvas id={id} ref={canvasElRef} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  selectedName: state.annotation.selectedName,
  fabricImages: state.stitchingView.fabricImages,
  presentFabricImages: state.stitchingView.presentFabricImages,
  zoom: state.stitchingView.zoom,
  leftScale: state.stitchingView.leftScale,
  centringLeftScale: state.stitchingView.centringLeftScale,
  topScale: state.stitchingView.topScale,
  pastFabricImages: state.stitchingView.pastFabricImages,
  futureFabricImages: state.stitchingView.futureFabricImages,
  canvasHeight: state.stitchingView.canvasHeight,
  reRender: state.stitchingView.reRender,
  sequenceInfo: state.annotation.selectedSequenceInfo,
  sequencePath: state.annotation.sequencePath,
  subsampleFactor: state.annotation.subsampleFactor,
  selectedAnnotationId: state.annotation.selectedAnnotationId,
  toggleAllUserFindingsVisibility: state.canvas.toggleAllUserFindingsVisibility,
  toggleMLFindingsVisibility: state.canvas.toggleMLFindingsVisibility
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onAddToHistory: (activeObjects: fabric.Object[], skipSmallMovements: boolean) => dispatch(addToHistory({ activeObjects, skipSmallMovements })),
  onUpdate: (rawImages: SequenceImage[] | null, isStitched: boolean, subsampleFactor: number) =>
    dispatch(updated({ rawImages, isStitched, subsampleFactor })),
  onSizeChanged: (canvasWidth: number, canvasHeight: number) => dispatch(sizeChanged({ canvasHeight, canvasWidth })),
  onImagesLoaded: () => dispatch(setImagesLoaded()),
  startImagesLoading: () => dispatch(setImagesLoading())
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(StitchedView));
